/* eslint-disable camelcase */
import React, { Component, Fragment, createRef } from "react";
import PropTypes from "prop-types";
import { SubNavBar } from "@components/common";
import Tooltip from "@components/common/Tooltip";
import { ClientService, OrganizationService, JurisdictionService, PaymentService } from "@services";
import HubspotService from "@services/HubspotService";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { ORGANIZATION_TYPE, JOB_INVOICED_TYPES, ORGANIZATION_TAB_MENU } from "@constants";
import utils from "@utils";
import {
	ReactSelect,
	Switch,
	EditImageModal,
	ImpersonateModal,
	EditAccountModal,
	Image,
	ScrollDetect,
	Loader,
	TabMenu,
} from "../../../components";
import {
	OfficerSyncModal,
	OrganizationSettingsModal,
	RefundModal,
	OrganizationFeesModal,
	EditSignupModal,
	ClientFeesModal,
	ReportAdminModal,
} from "./components";

import "./OrganizationDetails.scss";

const minStartDate = new Date();
minStartDate.setHours(0, 0, 0, 0);
minStartDate.setDate(minStartDate.getDate() + 1);
const displayCount = 25;
class OrganizationDetails extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			requestComplete: false,
			hasMore: true,
			editingName: false,
			editingType: false,
			editingJurisdiction: false,
			editingProcessingFees: [],
			validImageSelected: true,
			jurisdictionTerm: "",
			rkPayRateHistory: [],
			details: {},
			processingFees: [],
			showOrganizationFeesModal: false,
			showClientFeesModal: false,
			showReportAdminModal: false,
			selectedClient: {},
			newPFeeRateHistory: [],
			users: [],
			allUsers: [],
			jurisdictions: [],
			pageNumber: 1,
			showEditImageModal: false,
			imageToEdit: "",
			impersonatingUser: undefined,
			syncingOfficers: false,
			showOfficerSync: false,
			syncType: "officers",
			showHubspotTools: false,
			clients: [],
			clientHasMore: true,
			clientPageNumber: 1,
			clientsLoading: false,
			copiedToClipboard: false,
			showEditSignupModal: false,
			activeSubTab: "staff",
			currentBalance: null,
			balanceHistory: null,
			loadingBalance: true,
			payment: {},
			hubspot: {
				searchTerm: "",
				companies: [],
				contacts: [],
				searched: false,
				fetchingContacts: false,
				company: undefined,
			},
			showOrganizationSettingsModal: false,
			showRefundModal: false,
			checkingClientFutureJobs: {},
		};

		const {
			flags: {
				workersCompensationFee,
				newClientSignUp,
				clientLevelFees,
				enforceV2NonrkpayInvoicingEnabled,
			},
		} = props;
		this.hasWorkersCompensationFee = !!workersCompensationFee;
		this.hasClientLevelFees = !!clientLevelFees;
		this.hasNewClientSignUp = !!newClientSignUp;
		this.hasEnforceV2NonrkpayInvoicingEnabled = !!enforceV2NonrkpayInvoicingEnabled;

		this.orgNameRef = createRef();
		this.typeSelectRef = createRef();
		this._editUser = createRef();
		this.cancelJurisdictionRequest = null;
		this.originalProcessingFees = [];
	}

	componentDidMount() {
		const { params } = this.props;
		if (params && params.organizationId) {
			this.setState({ loading: true });
			this.getOrganizationDetails();
			this.getProcessingFeesHistory();
		}
	}

	toggleEditName = () => {
		this.setState(
			prevState => ({
				...prevState,
				editingName: !prevState.editingName,
			}),
			() => {
				const { editingName } = this.state;
				if (editingName) this.orgNameRef.current.focus();
			},
		);
	};

	toggleEditType = () => {
		this.setState(
			prevState => ({
				...prevState,
				editingType: !prevState.editingType,
			}),
			() => {
				const { editingType } = this.state;
				if (editingType) this.typeSelectRef.current.focus();
			},
		);
	};

	toggleEditJurisdiction = () => {
		this.setState(prevState => ({
			...prevState,
			editingJurisdiction: !prevState.editingJurisdiction,
		}));
	};

	toggleConfigureFeesModal = () =>
		this.setState(prevState => ({
			showOrganizationFeesModal: !prevState.showOrganizationFeesModal,
		}));

	toggleConfigureClientFeesModal = () =>
		this.setState(prevState => ({ showClientFeesModal: !prevState.showClientFeesModal }));

	toggleReportAdminModal = () =>
		this.setState(prevState => ({ showReportAdminModal: !prevState.showReportAdminModal }));

	selectClient = client => this.setState(() => ({ selectedClient: client }));

	/**
	 * Searches for a jurisdiction in the list
	 * @param {String} newTerm The term used to query the list by
	 */
	searchJurisdictions = newTerm => {
		this.setState({ jurisdictionTerm: newTerm }, () => {
			const { jurisdictionTerm } = this.state;
			if (jurisdictionTerm === "") {
				this.setState({ jurisdictions: [] });
			} else {
				if (this.cancelJurisdictionRequest) this.cancelJurisdictionRequest();
				JurisdictionService.getAll({
					searchValue: jurisdictionTerm,
					isActive: true,
					getCancelToken: c => {
						this.cancelJurisdictionRequest = c;
					},
				})
					.then(({ data }) => {
						this.cancelJurisdictionRequest = null;
						const jurisdictions = data.map(({ Id: value, Name: label }) => ({
							value,
							label,
						}));
						this.setState({ jurisdictions });
					})
					.catch(() => {});
			}
		});
	};

	handleEnterPress = field => event => {
		if (event.key === "Enter") {
			this.updateDetails(field)(event);
		}
	};

	/**
	 * Processes the selected image via `FileReader` and opens it in the Edit Image modal
	 * @param {Event} event The HTML event handled by this method
	 */
	prepImageUpload = event => {
		const { target } = event;
		if (!target.files || !target.files[0]) return;
		const [{ type }] = target.files;
		this.setState({ validImageSelected: type.includes("image") }, () => {
			if (type.includes("image")) {
				this.setState({ loading: true });

				const reader = new FileReader();
				reader.readAsDataURL(target.files[0]);
				reader.onload = ({ target: { result } }) => {
					this.openEditImageModal(result);
					target.value = "";
				};
			}
		});
	};

	copyToClipboard = async text => {
		await navigator.clipboard.writeText(text);
		this.setState({ copiedToClipboard: true });
	};

	openEditImageModal = imageToEdit => this.setState({ showEditImageModal: true, imageToEdit });

	closeEditImageModal = () =>
		this.setState({ showEditImageModal: false, imageToEdit: "", loading: false });

	/**
	 * Calls the API which handles uploads of a selected image
	 * @param {String} image The image blob that will be uploaded
	 */
	uploadImage = image => {
		const { lang } = global;
		const {
			params: { organizationId },
		} = this.props;
		OrganizationService.uploadImage(image, organizationId)
			.then(() => OrganizationService.getDetails(organizationId))
			.then(({ data: details }) => {
				this.setState({ loading: false, details });
			})
			.catch(() => {
				this.setState({ loading: false });
				utils.notify({
					type: "error",
					message: lang.t("Agency Photo updated successfully"),
					timeout: 1500,
				});
			});
	};

	verifyJurisdiction = jurisdictionId =>
		JurisdictionService.verifyJurisdictionIsAvailable(jurisdictionId);

	/**
	 * Handles all changes made to the input fields
	 * @param {String} field the field being updated
	 */
	updateDetails = field => event => {
		const { lang } = global;
		let value;
		if (
			field === "NonRKPayPaymentsEnabled" &&
			event.target.closest(".toggle-switch").classList.contains("disabled")
		)
			return;
		if (field === "Jurisdiction") value = event;
		else
			({
				target: { value },
			} = event);
		const {
			details: {
				BillingContact,
				PrimaryContact,
				Jurisdiction,
				SameDayPayEnabled,
				CanExceedRecurrentJobCreation,
				FloatInvoicePayEnabled,
				StripeSubscriptionEnabled,
				NonRKPayInvoicingEnabled,
				NonRKPayPaymentsEnabled,
				InvoicingV3Enabled,
				OfficerInvoicingV3Enabled,
				RKVerifiesEnabled,
				PayCodesEnabled,
				UsePTAEOCode,
				OnDutyUserScheduleEnabled,
				OfficerPortalEnabled,
				MFAEnforced,
				VehicleInvoicingEnabled,
				WorkerCompensationFeeEnabled,
				InstantPayEnabledV3,
				ManualReconciliationEnabled,
				AgencyInvoicingDisputes,
				InstantPayEnabledOfficerV3,
				...details
			},
		} = this.state;
		let valueUpdated = false;

		const body = {
			Id: details.Id,
			Name: details.Name,
			BillingContactId: BillingContact ? BillingContact.Id : null,
			PrimaryContactId: PrimaryContact ? PrimaryContact.Id : null,
			OrganizationType: details.Type,
			JurisdictionId: Jurisdiction ? Jurisdiction.Id : null,
			SameDayPayEnabled,
			CanExceedRecurrentJobCreation,
			FloatInvoicePayEnabled,
			NonRKPayInvoicingEnabled,
			NonRKPayPaymentsEnabled,
			StripeSubscriptionEnabled,
			InvoicingV3Enabled,
			OfficerInvoicingV3Enabled,
			RKVerifiesEnabled,
			PayCodesEnabled,
			UsePTAEOCode,
			OnDutyUserScheduleEnabled,
			OfficerPortalEnabled,
			MFAEnforced,
			VehicleInvoicingEnabled,
			WorkerCompensationFeeEnabled,
			InstantPayEnabledV3,
			ManualReconciliationEnabled,
			AgencyInvoicingDisputes,
			InstantPayEnabledOfficerV3,
		};

		switch (field) {
			case "Name":
				if (body.Name !== value) {
					valueUpdated = true;
					body.Name = value;
				}
				this.toggleEditName();
				break;
			case "Type":
				if (body.OrganizationType !== parseInt(value, 10)) {
					valueUpdated = true;
					body.OrganizationType = parseInt(value, 10);
				}
				break;
			case "Jurisdiction":
				// for jurisdictions, value is an object with the Id (value) and Name (label)
				if (body.JurisdictionId !== value.value) {
					valueUpdated = true;
				}
				break;
			case "SameDayPay":
				valueUpdated = true;
				body.SameDayPayEnabled = !body.SameDayPayEnabled;
				if (body.SameDayPayEnabled === false) {
					body.InstantPayEnabledV3 = false;
				}
				break;
			case "CanExceedRecurrentJobCreation":
				valueUpdated = true;
				body.CanExceedRecurrentJobCreation = !body.CanExceedRecurrentJobCreation;
				break;
			case "FloatInvoicePayEnabled":
				valueUpdated = true;
				body.FloatInvoicePayEnabled = !body.FloatInvoicePayEnabled;
				break;
			case "NonRKPayInvoicingEnabled":
				valueUpdated = true;
				body.NonRKPayInvoicingEnabled = !body.NonRKPayInvoicingEnabled;
				if (body.NonRKPayInvoicingEnabled === false) {
					body.NonRKPayPaymentsEnabled = false;
					body.InvoicingV3Enabled = false;
					body.VehicleInvoicingEnabled = false;
				}
				break;
			case "NonRKPayPaymentsEnabled":
				valueUpdated = true;
				body.NonRKPayPaymentsEnabled = !body.NonRKPayPaymentsEnabled;
				if (body.NonRKPayPaymentsEnabled === false) {
					body.InvoicingV3Enabled = false;
					body.VehicleInvoicingEnabled = false;
				}
				break;
			case "StripeSubscriptionEnabled":
				valueUpdated = true;
				body.StripeSubscriptionEnabled = !body.StripeSubscriptionEnabled;
				break;
			case "InvoicingV3Enabled":
				valueUpdated = true;
				body.InvoicingV3Enabled = !body.InvoicingV3Enabled;
				if (!body.InvoicingV3Enabled) {
					body.VehicleInvoicingEnabled = false;
					body.OfficerInvoicingV3Enabled = false;
				}
				break;
			case "RKVerifiesEnabled":
				valueUpdated = true;
				body.RKVerifiesEnabled = !body.RKVerifiesEnabled;
				break;
			case "OfficerInvoicingV3Enabled":
				valueUpdated = true;
				body.OfficerInvoicingV3Enabled = !body.OfficerInvoicingV3Enabled;
				break;
			case "PayCodesEnabled":
				valueUpdated = true;
				body.PayCodesEnabled = !body.PayCodesEnabled;
				break;
			case "UsePTAEOCode":
				valueUpdated = true;
				body.UsePTAEOCode = !body.UsePTAEOCode;
				break;
			case "OnDutyUserScheduleEnabled":
				valueUpdated = true;
				body.OnDutyUserScheduleEnabled = !body.OnDutyUserScheduleEnabled;
				break;
			case "OfficerPortalEnabled":
				valueUpdated = true;
				body.OfficerPortalEnabled = !body.OfficerPortalEnabled;
				break;
			case "MFAEnforced":
				valueUpdated = true;
				body.MFAEnforced = !body.MFAEnforced;
				break;
			case "VehicleInvoicingEnabled":
				valueUpdated = true;
				body.VehicleInvoicingEnabled = !body.VehicleInvoicingEnabled;
				break;
			case "WorkerCompensationFeeEnabled":
				valueUpdated = true;
				body.WorkerCompensationFeeEnabled = !body.WorkerCompensationFeeEnabled;
				break;
			case "InstantPayEnabledV3":
				valueUpdated = true;
				body.InstantPayEnabledV3 = !body.InstantPayEnabledV3;
				break;
			case "ManualReconciliationEnabled":
				valueUpdated = true;
				body.ManualReconciliationEnabled = !body.ManualReconciliationEnabled;
				break;
			case "AgencyInvoicingDisputes":
				valueUpdated = true;
				body.AgencyInvoicingDisputes = !body.AgencyInvoicingDisputes;
				break;
			case "InstantPayEnabledOfficerV3":
				valueUpdated = true;
				body.InstantPayEnabledOfficerV3 = !body.InstantPayEnabledOfficerV3;
				break;
			default:
				break;
		}

		if (valueUpdated) {
			if (field === "Jurisdiction") {
				this.verifyJurisdiction(value.value)
					.then(() => {
						body.JurisdictionId = value.value;
						this.setState({ jurisdictionTerm: "", editingJurisdiction: false });

						OrganizationService.updateDetails(body)
							.then(({ data }) => {
								this.setState({ details: data });
							})
							.catch(error => {
								let errorMessage = "";

								if (error.response.data?.ModelState) {
									Object.entries(error.response.data.ModelState).forEach(
										entry => {
											const [, val] = entry;
											if (val) {
												errorMessage += `${val} `;
											}
										},
									);
								} else {
									errorMessage = error.response.data.Message;
								}

								utils.notify({
									type: "error",
									message: `Unable to update details. ${errorMessage} Please try again.`,
									title: "Error",
									timeout: 1500,
								});
							});
					})
					.catch(({ response: { data } }) => {
						utils.notify({
							type: "error",
							message: data?.Message
								? `${data.Message}`
								: lang.t("An error occurred verifying the selected jurisdiction"),
							timeOut: 1500,
						});
					});
			} else {
				OrganizationService.updateDetails(body)
					.then(({ data }) => {
						this.setState({ details: data });
						if (
							NonRKPayInvoicingEnabled !== data.NonRKPayInvoicingEnabled ||
							WorkerCompensationFeeEnabled !== data.WorkerCompensationFeeEnabled ||
							ManualReconciliationEnabled !== data.ManualReconciliationEnabled
						) {
							this.setState({ clientPageNumber: 1 }, this.getOrganizationClients);
						}
					})
					.catch(error => {
						let errorMessage = "";

						if (error.response.data?.ModelState) {
							Object.entries(error.response.data.ModelState).forEach(entry => {
								const [, val] = entry;
								if (val) {
									errorMessage += `${val} `;
								}
							});
						} else {
							errorMessage = error.response.data.Message;
						}

						utils.notify({
							type: "error",
							message: `Unable to update details. ${errorMessage} Please try again.`,
							title: "Error",
							timeout: 1500,
						});
					});
			}
		}
	};

	/**
	 * Saves any changes made to the RKPay Rate
	 * @param {Object} data An object which contains each rate change
	 */
	saveRkPayRateChanges = (data, callback) => {
		const {
			params: { organizationId },
		} = this.props;
		const { TypeId, Rate: PercentageFee, EffectiveFrom } = data;
		const form = { TypeId, PercentageFee, EffectiveFrom };
		OrganizationService.changeRkPayRate(organizationId, form)
			.then(() => {
				this.getOrganizationDetails();
				if (callback) callback();
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message:
						"An error occurred. Please refresh the page to check if settings have been saved.",
					timeout: 1500,
				});
				if (callback) callback(true);
			});
	};

	/**
	 * Retrieves the organization details based on the unique organizationId
	 * @param {Boolean} getRkPayRateHistory Determines if the RKPay rate history needs to be retrieved for an organization
	 */
	getOrganizationDetails = (getRkPayRateHistory = true) => {
		const {
			params: { organizationId },
		} = this.props;
		OrganizationService.getDetails(organizationId)
			.then(({ data: details }) => {
				this.setState({ loading: false, requestComplete: true, details });
				const { LAW_ENFORCEMENT_DEPARTMENT, CLIENT } = ORGANIZATION_TYPE;
				if (details.Type === LAW_ENFORCEMENT_DEPARTMENT || details.Type === CLIENT) {
					OrganizationService.getProcessingFees(organizationId, {
						active: true,
						processingFeeId: undefined,
						pullFromOrganization: true,
					}).then(({ data }) => {
						this.originalProcessingFees = data;
						this.setState({ processingFees: data });
					});
				}
				if (details.Type === LAW_ENFORCEMENT_DEPARTMENT) {
					this.getAllOrganizationUsers();
				}
			})
			.catch(() => {
				this.setState({ loading: false, requestComplete: true });
				utils.notify({
					type: "error",
					message: "Unable to retrieve organization details.",
					timeOut: 1500,
				});
			});

		if (getRkPayRateHistory) {
			OrganizationService.getRkPayRates(organizationId)
				.then(({ data }) =>
					this.setState({
						rkPayRateHistory: data.map(({ PercentageFee, ...rate }) => ({
							...rate,
							Rate: PercentageFee,
						})),
					}),
				)
				.catch(() => {
					utils.notify({
						type: "error",
						message: "Unable to retrieve service fee history",
						title: "Error",
						timeout: 1500,
					});
				});
		}
	};

	/**
	 * Retrieves a paginated list of users per their organization
	 * This list will be used for syncing staff members to hubspot
	 * @param {Array} prevUsers A list of the previous existing users
	 * @param {Number} pageNumber Used to paginate the list
	 */
	getAllOrganizationUsers = (prevUsers = [], pageNumber = 1) => {
		const {
			params: { organizationId },
		} = this.props;
		const {
			details: { PrimaryContact, BillingContact },
		} = this.state;
		let allUsers = [...prevUsers];
		OrganizationService.getContacts(organizationId, { pageNumber, displayCount: 100 })
			.then(({ data: { ItemList } }) => {
				// update the Email key to EmailAddress for hubspot syncing purposes
				allUsers = [...allUsers, ...ItemList].map(user => ({
					...user,
					IsPrimaryContact:
						user.EmailAddress?.toLowerCase() ===
						PrimaryContact?.EmailAddress?.toLowerCase(),
					IsBillingContact:
						user.EmailAddress?.toLowerCase() ===
						BillingContact?.EmailAddress?.toLowerCase(),
				}));
				if (ItemList.length >= 100) {
					this.getAllOrganizationUsers(allUsers, pageNumber + 1);
				} else {
					this.setState({ allUsers });
				}
			})
			.catch(() => {});
	};

	/** Retrieves a paginated list of users associated with their organization */
	getOrganizationUsers = () => {
		const { lang } = global;
		const {
			params: { organizationId },
		} = this.props;
		const { pageNumber } = this.state;

		OrganizationService.getUsers(organizationId, { pageNumber, displayCount })
			.then(({ data: { ItemList } }) => {
				const newUsers = ItemList ?? [];
				this.setState(prevState => ({
					users: prevState.pageNumber > 1 ? [...prevState.users, ...newUsers] : newUsers,
					pageNumber: prevState.pageNumber + 1,
					hasMore: newUsers.length >= displayCount,
				}));
			})
			.catch(() => {
				this.setState({ users: [], pageNumber: 1, hasMore: false });
				utils.notify({
					type: "error",
					message: lang.t("Unable to retrieve organization users"),
					timeOut: 1500,
				});
			});
	};

	/**
	 * Retrieves a paginated list of clients which are associated with an organization
	 */
	getOrganizationClients = () => {
		const { lang } = global;
		const { clientPageNumber, clientsLoading } = this.state;
		const {
			params: { organizationId },
		} = this.props;

		// if a request is currently in progress prevent another request
		if (clientsLoading) return;
		this.setState({ clientsLoading: true });
		ClientService.getAll({
			organizationId,
			displayCount,
			pageNumber: clientPageNumber,
			includeInactive: false,
			getDetails: true,
		})
			.then(({ data: { ItemList } }) => {
				const newClients = ItemList ?? [];
				this.setState(prevState => ({
					clients:
						prevState.clientPageNumber > 1
							? [...prevState.clients, ...newClients]
							: newClients,
					clientPageNumber: prevState.clientPageNumber + 1,
					clientHasMore: newClients.length >= displayCount,
					clientsLoading: false,
				}));
			})
			.catch(() => {
				this.setState({ clients: [], clientHasMore: false, clientsLoading: false });
				utils.notify({
					type: "error",
					message: lang.t("Unable to retrieve clients"),
					title: "Error",
					timeout: 1500,
				});
			});
	};

	/**
	 * Checks if client has future officer invoicing jobs before enabling manual reconciliation
	 * @param {Number} clientId The unique id of the client being checked
	 * @param {Object} clientSettingsForm The settings form object
	 */
	checkClientHasFutureOfficerInvoicingJobs = (clientId, clientSettingsForm) => () => {
		const { lang } = global;

		// Set loading state for this specific client
		this.setState(prevState => ({
			checkingClientFutureJobs: {
				...prevState.checkingClientFutureJobs,
				[clientId]: true,
			},
		}));

		ClientService.hasFutureOfficerInvoicingJobs(clientId)
			.then(({ data }) => {
				if (data.HasFutureOfficerInvoicingJobs) {
					utils.notify({
						type: "error",
						message: data.Message,
						timeout: 5000,
					});
					return;
				}
				this.toggleClientSettings(clientId, clientSettingsForm)();
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error checking for future officer invoicing jobs"),
					timeout: 5000,
				});
			})
			.finally(() => {
				// Clear loading state for this client
				this.setState(prevState => ({
					checkingClientFutureJobs: {
						...prevState.checkingClientFutureJobs,
						[clientId]: false,
					},
				}));
			});
	};

	/**
	 * Handles the onClick logic for the enforce V2 non-RKPay invoicing toggle
	 * @param {Object} client The client object
	 * @param {Event} event The click event
	 */
	handleOnClickEnforceV2NonRKPayInvoicing = (client, event) => {
		const { details } = this.state;
		const clientSettingsForm = {
			instantPay: client.SameDayPayEnabled,
			floatPay: client.FloatInvoicePayEnabled,
			isWorkerCompensationFeeEnabled: client?.IsWorkerCompensationFeeEnabled,
			enforceV2NonRKPayInvoicing: !client.EnforceV2NonRKPayInvoicing,
		};

		if (details.OfficerInvoicingV3Enabled && !client.EnforceV2NonRKPayInvoicing) {
			this.checkClientHasFutureOfficerInvoicingJobs(client.Id, clientSettingsForm)(event);
		} else {
			this.toggleClientSettings(client.Id, clientSettingsForm)(event);
		}
	};

	/**
	 * Toggles instantPay settings for an individual client
	 * @param {Number} clientId The unique id of the client being toggled
	 * @param {Object} data for Instant Pay and Invoice Float
	 */
	toggleClientSettings = (clientId, clientSettingsForm) => e => {
		if (e) e.stopPropagation();
		const { instantPay, floatPay, enforceV2NonRKPayInvoicing } =
			clientSettingsForm;
		const body = {
			Id: clientId,
			SameDayPayEnabled: instantPay,
			FloatInvoicePayEnabled: floatPay,
			EnforceV2NonRKPayInvoicing: enforceV2NonRKPayInvoicing,
		};

		if (this.hasWorkersCompensationFee) {
			body.IsWorkerCompensationFeeEnabled = clientSettingsForm.isWorkerCompensationFeeEnabled;
		}

		OrganizationService.updateInstantPay(body)
			.then(
				({
					data: {
						SameDayPayEnabled,
						FloatInvoicePayEnabled,
						IsWorkerCompensationFeeEnabled,
						EnforceV2NonRKPayInvoicing,
					},
				}) => {
					this.setState(prevState => ({
						clients: prevState.clients.map(client => {
							if (client.Id === clientId) {
								return {
									...client,
									SameDayPayEnabled,
									FloatInvoicePayEnabled,
									IsWorkerCompensationFeeEnabled,
									EnforceV2NonRKPayInvoicing,
								};
							}
							return client;
						}),
					}));
				},
			)
			.catch(() => {});
	};

	/**
	 * Handles conversion of dates to the proper format
	 * @param {String} date the date being converted
	 * @param {Boolean} includeTime Determines if time will be included in date conversion
	 * @returns {String} The date converted to local time
	 */
	convertToLocalDate = (date = new Date(), includeTime = false) => {
		const dateFormat = {
			day: "numeric",
			month: "short",
			year: "numeric",
		};

		const dateTimeFormat = {
			...dateFormat,
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		};
		// date should be UTC time, verify by creating from ISO string
		const utcDate = new Date(date.toISOString());
		const localDate = new Date(utcDate.getTime() - new Date().getTimezoneOffset() * 60000);

		return localDate.toLocaleString(undefined, includeTime ? dateTimeFormat : dateFormat);
	};

	getProcessingFeesHistory = () => {
		const {
			params: { organizationId },
		} = this.props;
		OrganizationService.getProcessingFees(organizationId, {
			active: false,
			pullFromOrganization: true,
		})
			.then(({ data }) => this.setState({ newPFeeRateHistory: data }))
			.catch(() => {});
	};

	/**
	 *
	 * @param {*} data
	 */
	updateProcessingFees = (data, callback) => {
		const { LAW_ENFORCEMENT_DEPARTMENT, CLIENT } = ORGANIZATION_TYPE;
		const {
			details: { Type },
		} = this.state;
		const {
			params: { organizationId },
		} = this.props;
		const { TypeId: ProcessingFeeId, Rate, EffectiveFrom } = data;

		if (Type === LAW_ENFORCEMENT_DEPARTMENT || Type === CLIENT) {
			OrganizationService.saveProcessingFee(organizationId, {
				ProcessingFeeId,
				Rate,
				EffectiveFrom,
				Modifier: 0,
			})
				.then(({ data: newProcessingFees }) => {
					this.originalProcessingFees = newProcessingFees;
					this.setState(
						{ processingFees: newProcessingFees },
						this.getProcessingFeesHistory,
					);
					if (callback) callback();
				})
				.catch(() => {
					utils.notify({
						type: "error",
						message:
							"An error occurred. Please refresh the page to check if settings have been saved.",
						timeout: 1500,
					});
					if (callback) callback(true);
				});
		}
	};

	handleSearchTermChange = searchTerm =>
		this.setState(prevState => ({ hubspot: { ...prevState.hubspot, searchTerm } }));

	/**
	 * Searches for the company listed in hubspot
	 * @param {Array} prevCompanies A list of companies that were previously displayed after a search was done
	 * @param {Boolean} after Used for paginating the list of companies
	 */
	searchCompanies = (prevCompanies = [], after = undefined) => {
		let companies = [...prevCompanies];
		const {
			hubspot: { searchTerm },
		} = this.state;
		if (searchTerm.trim().length === 0) return;

		HubspotService.searchCompanies(searchTerm, after)
			.then(({ data: { results, paging } }) => {
				companies = [...companies, ...results];
				if (paging) {
					this.searchCompanies(companies, paging.next.after);
				} else {
					this.setState(prevState => ({
						hubspot: {
							...prevState.hubspot,
							companies,
							searched: true,
						},
					}));
				}
			})
			.catch(() =>
				utils.notify({
					type: "error",
					message: "Error fetching HubSpot companies",
					timeOut: 2000,
				}),
			);
	};

	/**
	 * Selects the hubspot company which a user would like to sync its officers with
	 * @param {Number} id The unique id of the selected company
	 */
	selectCompany = id => {
		const {
			hubspot: { companies },
		} = this.state;
		const company = companies.find(co => co.id === id);
		this.setState(prevState => ({
			hubspot: {
				...prevState.hubspot,
				searchTerm: "",
				companies: [],
				searched: false,
				company,
			},
		}));
	};

	/** Resets and clears the company that was previously being searched */
	clearCompany = () =>
		this.setState(prevState => ({
			hubspot: {
				...prevState.hubspot,
				company: undefined,
				contacts: [],
			},
		}));

	syncOfficers = syncType => {
		this.setState({ syncingOfficers: true, showOfficerSync: true, syncType });
	};

	/**
	 * Hides the list of officer being synced after closing out the Officer Sync Modal
	 * @param {Array} results The results returned after performing an officer sync
	 */
	onHideOfficerSync = results =>
		this.setState({ syncingOfficers: false, showOfficerSync: false }, () => {
			const {
				hubspot: { contacts: currentContacts },
			} = this.state;
			// Update any existing entries with data from sync results
			const contacts = currentContacts.map(contact => {
				const update = results.find(result => result.Id === contact.id);
				if (update) {
					return {
						...contact,
						properties: {
							...contact.properties,
							contact_type: "Officer - App User",
							officer_id: update.OfficerId,
							firstname: update.FirstName,
							lastname: update.LastName,
							email: update.EmailAddress,
						},
					};
				}
				return contact;
			});
			// For any results that are not part of the current contacts array, add an object that contains only the essential properties (returned in the result)
			results.forEach(result => {
				if (!contacts.some(contact => result.Id === contact.id) && result.Id) {
					contacts.push({
						id: result.Id,
						properties: {
							contact_type: "Officer - App User",
							officer_id: result.OfficerId,
							firstname: result.FirstName,
							lastname: result.LastName,
							email: result.EmailAddress,
						},
					});
				}
			});
			this.setState(prevState => ({ hubspot: { ...prevState.hubspot, contacts } }));
		});

	toggleImpersonateModal = user => {
		this.setState(prevState => {
			if (prevState.impersonatingUser) return { impersonatingUser: undefined };
			return { impersonatingUser: user.userId };
		});
	};

	uploadDocument = event => {
		const file = event?.target?.files[0] ?? null;
		const {
			params: { organizationId },
		} = this.props;
		if (!file) return;

		const { lang } = global;
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => {
			OrganizationService.uploadPTAEOCodes(file, organizationId)
				.then(({ data: { Message } }) => {
					utils.notify({
						message: Message,
						type: "success",
						title: lang.t("PTAEO codes imported successfully"),
					});
				})
				.catch(() => {
					utils.notify({
						message: lang.t("Error importing PTAEO codes"),
						type: "error",
					});
				});
		};
	};

	isFinancialAdmin = () => {
		const { user } = this.props;
		const validUserIds = [38583, 32033, 32031, 34375];
		return validUserIds.includes(user.Id);
	};

	toggleOrganizationSettingsModal = () =>
		this.setState(prevState => ({
			showOrganizationSettingsModal: !prevState.showOrganizationSettingsModal,
		}));

	toggleRefundModal = balance =>
		this.setState(prevState => ({
			showRefundModal: !prevState.showRefundModal,
			payment: balance,
		}));

	toggleEditSignupModal = () =>
		this.setState(prevState => ({ showEditSignupModal: !prevState.showEditSignupModal }));

	formatJobInvoicedType = jobInvoicedTypeId => {
		switch (jobInvoicedTypeId) {
			case JOB_INVOICED_TYPES.OFFICER_INVOICING:
				return "Officer Invoicing";
			case JOB_INVOICED_TYPES.AGENCY_INVOICING:
				return "Agency Invoicing";
			case JOB_INVOICED_TYPES.SCHEDULE_ONLY:
				return "Schedule Only";
			default:
				return "-";
		}
	};

	getBalance = async newSubTab => {
		const { params } = this.props;
		const { currentBalance } = this.state;
		if (
			newSubTab === ORGANIZATION_TAB_MENU.find(tab => tab.tabNumber === 1).type &&
			currentBalance == null
		) {
			PaymentService.getOrganizationBalance(params.organizationId)
				.then(data => {
					this.setState({ currentBalance: data.data });
				})
				.catch(() => {
					utils.notify({
						message: "Error Getting Organization Balance",
						type: "error",
					});
				});
		}
	};

	getBalanceHistory = async newSubTab => {
		const { params } = this.props;
		const { balanceHistory } = this.state;
		if (
			newSubTab === ORGANIZATION_TAB_MENU.find(tab => tab.tabNumber === 1).type &&
			balanceHistory == null
		) {
			PaymentService.getBalanceHistory(params.organizationId)
				.then(data => {
					this.setState({ balanceHistory: data.data });
				})
				.catch(() => {
					utils.notify({
						message: "Error getting balance",
						type: "error",
					});
				})
				.finally(() => {
					this.setState({ loadingBalance: false });
				});
		}
	};

	onClickTab = async ({ tabNumber }) => {
		const newSubTab = ORGANIZATION_TAB_MENU.find(tab => tab.tabNumber === tabNumber).type;
		const { activeSubTab } = this.state;
		if (newSubTab !== activeSubTab) {
			this.getBalance(newSubTab).then(
				this.getBalanceHistory(newSubTab).then(this.setState({ activeSubTab: newSubTab })),
			);
		}
	};

	render() {
		const { lang } = global;
		const {
			router: { goBack },
			user,
		} = this.props;
		const {
			params: { organizationId },
		} = this.props;
		const {
			loading,
			requestComplete,
			users,
			allUsers,
			jurisdictions,
			hasMore,
			editingName,
			editingType,
			editingJurisdiction,
			validImageSelected,
			jurisdictionTerm,
			details: { PrimaryContact, BillingContact, EffectiveServiceFees, Type, ...details },
			processingFees,
			showOrganizationFeesModal,
			showClientFeesModal,
			showReportAdminModal,
			selectedClient,
			newPFeeRateHistory,
			rkPayRateHistory,
			showEditImageModal,
			imageToEdit,
			impersonatingUser,
			syncingOfficers,
			showOfficerSync,
			hubspot,
			showHubspotTools,
			clients,
			clientHasMore,
			showOrganizationSettingsModal,
			showRefundModal,
			showEditSignupModal,
			activeSubTab,
			currentBalance,
			loadingBalance,
			balanceHistory,
			payment,
			syncType,
			copiedToClipboard,
			checkingClientFutureJobs,
		} = this.state;

		let taxExceptionClass = "rkf rkf-cross-solid text-danger";
		switch (details.TaxExceptionStatus) {
			case 1:
				taxExceptionClass = "rkf rkf-clock-revert text-warning";
				break;
			case 2:
				taxExceptionClass = "rkf rkf-check-mark text-success";
				break;
			default:
				break;
		}

		const { CLIENT, LAW_ENFORCEMENT_DEPARTMENT } = ORGANIZATION_TYPE;

		const organizationTabs = ORGANIZATION_TAB_MENU.map(item => {
			if (item.type === "RKPayPrepay") return { ...item, text: lang.t("RKPay Prepay") };
			return { ...item, text: lang.t("Staff") };
		});

		return (
			<Fragment>
				<Loader active={loading} />
				<SubNavBar
					onClickBack={() => goBack()}
					backText={lang.t("Back")}
					className="sub-nav-bar-with-tabs"
				>
					<div className="flex-grow-1">
						<TabMenu
							tabs={organizationTabs}
							activeTab={
								organizationTabs.find(tab => tab.type === activeSubTab)
									.tabNumber
							}
							onClickTab={this.onClickTab}
						/>
					</div>
				</SubNavBar>
				<div
					id="organization-details"
					className="container"
				>
					<Choose>
						<When condition={requestComplete && Object.keys(details).length === 0}>
							<div className="row text-center">
								<div className="col-sm-6 col-sm-offset-3 h3">
									{lang.t("No organization details")}
								</div>
							</div>
						</When>
						<When condition={requestComplete}>
							<div className="row">
								<div className="col-sm-5 organization-panel">
									<div className="panel panel-default">
										<div className="panel-body container-fluid">
											<div className="row">
												<div className="col-xs-6">
													<If condition={details.Created}>
														<span
															title={`Created on ${this.convertToLocalDate(new Date(details.Created), true)}`}
														>
															{lang.t("Created")}{" "}
															{this.convertToLocalDate(
																new Date(details.Created),
															)}
														</span>
													</If>
												</div>
												<div className="col-xs-6 text-right">
													{details.Id && `ID ${details.Id}`}
												</div>
											</div>
											<div className="row">
												<div className="col-sm-6 col-sm-offset-3 text-center form-group image-uploader">
													<label
														htmlFor="change-org-img"
														className="organization-logo"
													>
														<Image
															src={details.OrganizationLogo}
															fallbackSrc="default"
														/>
														<div className="blur">
															{lang.t("Edit Logo")}
														</div>
														<input
															id="change-org-img"
															name="change-org-img"
															type="file"
															accept=".jpeg,.jpg,.png,.tif,.tiff"
															onChange={this.prepImageUpload}
															className="form-control hidden"
														/>
													</label>
												</div>
												<If condition={!validImageSelected}>
													<div className="col-xs-12 text-danger text-center">
														{lang.t(
															"Please ensure the selected image has one of the following file extensions:",
														)}
														&nbsp;
														{lang.t("jpeg, jpg, png, tif, tiff")}
													</div>
												</If>
											</div>
											<div className="row">
												<Choose>
													<When condition={editingName}>
														<div className="form-group col-xs-8 col-xs-offset-2">
															<input
																ref={this.orgNameRef}
																type="text"
																className="form-control display-inline-block"
																placeholder={details.Name}
																defaultValue={details.Name}
																onKeyPress={this.handleEnterPress(
																	"Name",
																)}
																onBlur={this.updateDetails("Name")}
															/>
														</div>
														<button
															type="button"
															className="btn btn-link col-xs-2 cancel-edit-btn text-left"
															onClick={this.toggleEditName}
															title="Discard changes"
														>
															<i className="rkf rkf-cross-solid" />
														</button>
													</When>
													<Otherwise>
														<div className="col-sm-10 col-sm-offset-1 text-center">
															<span className="h4">
																{details.Name}
															</span>
															<button
																type="button"
																className="btn btn-link edit-btn"
																onClick={this.toggleEditName}
															>
																<i className="rkf rkf-edit" />
															</button>
														</div>
													</Otherwise>
												</Choose>
												<Choose>
													<When condition={editingType}>
														<div className="form-group col-xs-8 col-xs-offset-2">
															<select
																ref={this.typeSelectRef}
																className="form-control"
																onChange={this.updateDetails(
																	"Type",
																)}
																value={details.Type}
																disabled
															>
																{ORGANIZATION_TYPE.map(
																	({ value, label }) => (
																		<option
																			key={`typeOption_${value}`}
																			value={value}
																		>
																			{label}
																		</option>
																	),
																)}
															</select>
														</div>
														<button
															type="button"
															className="btn btn-link col-xs-2 cancel-edit-btn text-left"
															onClick={this.toggleEditType}
															title="Stop editing"
														>
															<i className="rkf rkf-cross-solid" />
														</button>
													</When>
													<Otherwise>
														<div className="col-sm-10 col-sm-offset-1 text-center">
															<span className="h5 text-gray">
																{details.TypeDescription}
															</span>
															<button
																type="button"
																className="btn btn-link edit-btn hidden"
																onClick={this.toggleEditType}
															>
																<i className="rkf rkf-edit" />
															</button>
														</div>
													</Otherwise>
												</Choose>
											</div>
											{/* <If condition={details.Jurisdiction}> */}
											<div className="row margin-bottom-md">
												<Choose>
													<When condition={editingJurisdiction}>
														<ReactSelect
															name="jurisdictionTerm"
															value={jurisdictionTerm}
															className="col-xs-10"
															options={jurisdictions}
															onInputChange={this.searchJurisdictions}
															onChange={this.updateDetails(
																"Jurisdiction",
															)}
															placeholder="Search jurisdictions"
														/>
														<button
															type="button"
															className="btn btn-link col-xs-2 cancel-edit-btn text-left"
															onClick={this.toggleEditJurisdiction}
															title="Stop editing"
														>
															<i className="rkf rkf-cross-solid" />
														</button>
													</When>
													<Otherwise>
														<div className="col-xs-12">
															<h5>
																<strong>
																	{lang.t("Jurisdiction")}
																</strong>
															</h5>
															{details.Jurisdiction?.Name ?? ""}
															<button
																type="button"
																className="btn btn-link edit-btn"
																onClick={
																	this.toggleEditJurisdiction
																}
															>
																<i className="rkf rkf-edit" />
															</button>
														</div>
													</Otherwise>
												</Choose>
											</div>
											<If condition={this.hasNewClientSignUp}>
												<div className="row margin-bottom-md">
													<div className="col-xs-12">
														<h5>
															<strong>
																{lang.t("Client Sign-up URL_")}
															</strong>
														</h5>
														<div className="signup-container">
															<a
																className="signup-link"
																href={details.FullClientSignupUrl}
																target="_blank"
																rel="noopener noreferrer"
															>
																{details.FullClientSignupUrl}
															</a>
															<div className="signup-actions">
																<If
																	condition={
																		details.FullClientSignupUrl
																	}
																>
																	<If
																		condition={
																			copiedToClipboard
																		}
																	>
																		<Tooltip
																			content={lang.t(
																				"Copied",
																			)}
																		>
																			<button
																				type="button"
																				className="btn btn-link"
																				onClick={() =>
																					this.copyToClipboard(
																						details.FullClientSignupUrl,
																					)
																				}
																			>
																				<i className="glyphicon glyphicon-copy" />
																			</button>
																		</Tooltip>
																	</If>
																	<If
																		condition={
																			!copiedToClipboard
																		}
																	>
																		<Tooltip
																			content={lang.t(
																				"Copy to clipboard",
																			)}
																		>
																			<button
																				type="button"
																				className="btn btn-link"
																				onClick={() =>
																					this.copyToClipboard(
																						details.FullClientSignupUrl,
																					)
																				}
																			>
																				<i className="glyphicon glyphicon-copy" />
																			</button>
																		</Tooltip>
																	</If>
																</If>
																<Tooltip
																	content={lang.t(
																		"Edit Sign-up URL",
																	)}
																>
																	<button
																		type="button"
																		className="btn btn-link"
																		onClick={() =>
																			this.toggleEditSignupModal()
																		}
																	>
																		<i className="rkf rkf-edit" />
																	</button>
																</Tooltip>
															</div>
														</div>
													</div>
												</div>
											</If>
											{/* </If> */}
											<div className="row margin-bottom-lg">
												<If condition={PrimaryContact}>
													<div className="col-sm-6 org-contact">
														<h5>
															<strong>
																{lang.t("Primary Contact")}
															</strong>
														</h5>
														<div>
															{`${PrimaryContact.FirstName} ${PrimaryContact.LastName}`}
															<br className="visible-xs visible-sm" />
															<span className="hidden-xs hidden-sm">
																&nbsp;
															</span>
															{`(${PrimaryContact.Id})`}
														</div>
														<If condition={PrimaryContact.EmailAddress}>
															<div className="truncate">
																<a
																	href={`mailto:${PrimaryContact.EmailAddress}`}
																>
																	{PrimaryContact.EmailAddress}
																</a>
															</div>
														</If>
													</div>
												</If>
												<If condition={BillingContact}>
													<div className="col-sm-6 org-contact">
														<h5>
															<strong>
																{lang.t("Billing Contact")}
															</strong>
														</h5>
														<div>
															{`${BillingContact.FirstName} ${BillingContact.LastName}`}
															<br className="visible-xs visible-sm" />
															<span className="hidden-xs hidden-sm">
																&nbsp;
															</span>
															{`(${BillingContact.Id})`}
														</div>
														<If condition={BillingContact.EmailAddress}>
															<div className="truncate">
																<a
																	href={`mailto:${BillingContact.EmailAddress}`}
																>
																	{BillingContact.EmailAddress}
																</a>
															</div>
														</If>
													</div>
												</If>
											</div>
											<If condition={details.TaxExceptionStatus > 0}>
												<div className="row">
													<div className="col-sm-12">
														<i className={taxExceptionClass} />
														<span>
															{`Tax Exemption: ${details.TaxExceptionStatusDescription}`}
														</span>
													</div>
												</div>
											</If>
											<div className="row margin-bottom-lg">
												<div className="col-xs-12 text-center">
													<button
														type="button"
														className="btn btn-primary btn-big modal-btn"
														onClick={() =>
															this.toggleOrganizationSettingsModal()
														}
													>
														<span>
															{lang.t("Organization Settings")}
														</span>
													</button>
												</div>
											</div>
											<div className="row margin-bottom-lg">
												<div className="col-xs-12 text-center">
													<button
														type="button"
														className="btn btn-primary btn-big modal-btn"
														onClick={() =>
															this.toggleConfigureFeesModal()
														}
													>
														<span>{lang.t("Configure Fees")}</span>
													</button>
												</div>
											</div>
											<If condition={Type !== CLIENT}>
												<div className="row">
													<div className="col-xs-12 text-center">
														<button
															type="button"
															className="btn btn-primary btn-big modal-btn"
															onClick={() =>
																this.toggleReportAdminModal()
															}
														>
															<span>
																{lang.t("Configure Reports")}
															</span>
														</button>
													</div>
												</div>
											</If>
										</div>
									</div>
								</div>
								<div className="col-sm-7">
									<If condition={Type === LAW_ENFORCEMENT_DEPARTMENT}>
										<button
											type="button"
											className="btn btn-link text-left hc-toggle"
											onClick={() =>
												this.setState(prevState => ({
													showHubspotTools: !prevState.showHubspotTools,
												}))
											}
										>
											<span className="h3">
												{lang.t("Hubspot Integration")}
											</span>
											<i
												className={`text-primary glyphicon glyphicon-chevron-${showHubspotTools ? "up" : "down"}`}
											/>
										</button>
										<div
											id="hubspot"
											className={`${showHubspotTools ? "" : "hidden"}`}
										>
											<div className="row">
												<div className="col-sm-12 col-md-6 col-lg-7 form-group">
													<Choose>
														<When condition={!hubspot.company}>
															<div className="input-group">
																<input
																	type="text"
																	name="hubspot-co"
																	className="form-control"
																	placeholder="Find company"
																	value={hubspot.searchTerm}
																	onChange={({
																		target: { value },
																	}) =>
																		this.handleSearchTermChange(
																			value,
																		)
																	}
																	onKeyPress={({ key }) => {
																		if (key === "Enter")
																			this.searchCompanies();
																	}}
																/>
																<span className="input-group-btn">
																	<button
																		type="button"
																		className="btn btn-primary"
																		onClick={() =>
																			this.searchCompanies()
																		}
																	>
																		<i className="glyphicon glyphicon-search" />
																	</button>
																</span>
															</div>
														</When>
														<Otherwise>
															{hubspot.company.properties.name}
															<If
																condition={
																	hubspot.company.properties
																		.domain
																}
															>
																&nbsp;(
																<em>
																	{
																		hubspot.company.properties
																			.domain
																	}
																</em>
																)
															</If>
															<button
																type="button"
																className={`btn btn-link${syncingOfficers ? " hidden" : ""}`}
																onClick={this.clearCompany}
															>
																{lang.t("Change")}
															</button>
														</Otherwise>
													</Choose>
												</div>
												<div className="col-xs-12 col-md-6 col-lg-5 form-group text-right button-container">
													<button
														type="button"
														className="btn btn-default"
														disabled={
															!hubspot.company ||
															hubspot.fetchingContacts
														}
														onClick={() =>
															this.syncOfficers("officers")
														}
													>
														{lang.t("Sync Officers")}
													</button>

													<If condition={allUsers.length > 0}>
														<button
															type="button"
															className="btn btn-default"
															disabled={
																!hubspot.company ||
																hubspot.fetchingContacts
															}
															onClick={() =>
																this.syncOfficers("staff")
															}
														>
															{lang.t("Sync Staff")}
														</button>
													</If>
												</div>
											</div>
											<If
												condition={
													hubspot.searched &&
													hubspot.companies.length === 0
												}
											>
												<div className="row">
													<div className="col-sm-12 text-muted">
														{lang.t("No companies found")}
													</div>
												</div>
											</If>
											<If
												condition={
													hubspot.searched && hubspot.companies.length > 0
												}
											>
												<For
													each="company"
													of={hubspot.companies}
												>
													<div
														className="row hs-co"
														key={`hubspot_${company.id}`}
													>
														<div className="col-sm-12">
															<button
																type="button"
																className="btn btn-link"
																onClick={() =>
																	this.selectCompany(company.id)
																}
															>
																{company.properties.name}
																<If
																	condition={
																		company.properties.domain
																	}
																>
																	&nbsp;(
																	<em>
																		{company.properties.domain}
																	</em>
																	)
																</If>
															</button>
														</div>
													</div>
												</For>
											</If>
										</div>
									</If>
									<If
										condition={
											activeSubTab ===
											ORGANIZATION_TAB_MENU.find(tab => tab.tabNumber === 0)
												.type
										}
									>
										<h3>{lang.t("Staff Members")}</h3>
										<div id="users-table">
											<div className="row v-align-sm hidden-xs staff-headers-sm">
												<div className="col-sm-2">
													<strong>{lang.t("User ID")}</strong>
												</div>
												<div className="col-sm-7">
													<strong>{lang.t("EMail")}</strong>
												</div>
												<div className="col-sm-3">
													<strong>{lang.t("Role")}</strong>
												</div>
											</div>
											<div className="table-overflow">
												<For
													each="user"
													of={users}
												>
													<div
														key={`orguser-${user.Id}`}
														role="button"
														tabIndex="0"
														onClick={() => {
															this._editUser.current.open(user.Id);
														}}
														onKeyPress={({ key }) => {
															if (key === "Enter")
																this._editUser.current.open(
																	user.Id,
																);
														}}
														className="row v-align-sm"
													>
														<div className="col-sm-2">
															<span className="visible-xs-inline">
																ID&nbsp;
															</span>
															<button
																type="button"
																className="btn btn-link imp-user"
																onClick={e => {
																	e.stopPropagation();
																	this.toggleImpersonateModal({
																		userId: user.Id,
																	});
																}}
															>
																{user.Id}
															</button>
														</div>
														<div className="col-xs-7 staff-email">
															<a
																href={`mailto:${user.Email}`}
																onClick={e => e.stopPropagation()}
															>
																{user.Email}
															</a>
														</div>
														<div className="col-xs-6 col-sm-3 text-right">
															<Choose>
																<When condition={user.IsViewOnly}>
																	{lang.t("View Only")}
																</When>
																<When condition={user.IsAdmin}>
																	{lang.t("Admin")}
																</When>
																<When
																	condition={user.IsCoordinator}
																>
																	{lang.t("Coordinator")}
																</When>
																<Otherwise>
																	{lang.t("Staff")}
																</Otherwise>
															</Choose>
														</div>
													</div>
												</For>
												<If condition={hasMore}>
													<ScrollDetect
														className="text-center"
														onVisible={this.getOrganizationUsers}
													>
														<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
													</ScrollDetect>
												</If>
											</div>
										</div>
									</If>
									<If
										condition={
											activeSubTab ===
											ORGANIZATION_TAB_MENU.find(tab => tab.tabNumber === 1)
												.type
										}
									>
										<h3>{lang.t("Total Balance")}</h3>
										<If condition={loadingBalance}>
											<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
										</If>
										<If condition={currentBalance != null}>
											<h4>{utils.currencyFormatter(currentBalance)}</h4>
										</If>
										<h3>{lang.t("Payments")}</h3>
										<div id="payments-table">
											<div className="row v-align-sm hidden-xs staff-headers-sm">
												<div className="col-sm-2">
													<strong>{lang.t("Date")}</strong>
												</div>
												<div className="col-sm-3">
													<strong>{lang.t("Amount")}</strong>
												</div>
												<div className="col-sm-4">
													<strong>{lang.t("Stripe ID / Type")}</strong>
												</div>
												<div className="col-sm-3">
													<strong>{lang.t("Action")}</strong>
												</div>
											</div>
											<div className="table-overflow">
												<If
													condition={
														balanceHistory == null || loadingBalance
													}
												>
													<div className="text-center">
														<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
													</div>
												</If>
												<If
													condition={
														balanceHistory != null &&
														currentBalance != null
													}
												>
													<For
														each="balance"
														index="index"
														of={balanceHistory.ItemList}
													>
														<div
															key={`balance-${index}`}
															className="row v-align-sm"
														>
															<div className="col-sm-2">
																{utils
																	.parseUTCDateToTimeZone(
																		balance.CreatedDateUTC,
																		"UTC",
																	)
																	.format("MM/DD/YYYY")}
																<br />
																<div className="time">
																	<i className="rkf rkf-clock text-gray" />
																	{utils
																		.parseUTCDateToTimeZone(
																			balance.CreatedDateUTC,
																			"UTC",
																		)
																		.format("h:mm A")}
																</div>
															</div>
															<div
																className={`col-sm-3 multiline ${balance.Amount >= 0 ? "text-success" : "text-danger"}`}
															>
																{balance.Amount >= 0
																	? `+ ${utils.currencyFormatter(balance.Amount)}`
																	: `- ${utils.currencyFormatter(Math.abs(balance.Amount))}`}
															</div>
															<div className="col-sm-4">
																<If
																	condition={
																		balance.StripeTransactionId
																	}
																>
																	<span className="multiline">
																		{
																			balance.StripeTransactionId
																		}
																	</span>
																</If>
																<If
																	condition={
																		!balance.StripeTransactionId
																	}
																>
																	<a
																		href={`/payments/${balance.InvoiceId}`}
																		className="btn btn-link padding-0"
																		aria-label={`Link to Invoice #${balance.InvoiceId}`}
																		target="_blank"
																		rel="noopener noreferrer"
																	>
																		Invoice #{balance.InvoiceId}
																	</a>
																</If>
															</div>
															<div className="col-sm-3">
																<If condition={balance.Amount > 0}>
																	<button
																		type="button"
																		className="btn btn-primary btn-medium"
																		onClick={() =>
																			this.toggleRefundModal(
																				balance,
																			)
																		}
																	>
																		<span>
																			{lang.t("Refund")}
																		</span>
																	</button>
																</If>
															</div>
														</div>
													</For>
												</If>
											</div>
										</div>
									</If>
								</div>
							</div>
							<div className="row">
								<div className="col-sm-12">
									<If condition={Type !== CLIENT}>
										<h3>{lang.t("Clients")}</h3>
										<div id="clients-table">
											<div className="row v-align-sm hidden-xs staff-headers-sm">
												<div className="col-sm-2">
													<strong>{lang.t("ID")}</strong>
												</div>
												<div className="col-sm-3">
													<strong>{lang.t("Name")}</strong>
												</div>
												<div className="col-sm-2">
													<strong>{lang.t("Default Invoicing")}</strong>
												</div>
												<div className="col-sm-3">
													<div className="row toogle-row">
														{this.isFinancialAdmin() && (
															<div className="col-sm-3">
																<strong>${lang.t("Float")}</strong>
															</div>
														)}
														<div className="col-sm-3">
															<strong>{lang.t("Instant Pay")}</strong>
														</div>
														<If
															condition={
																this.hasWorkersCompensationFee &&
																details.WorkerCompensationFeeEnabled
															}
														>
															<div className="col-sm-3">
																<strong>{lang.t("WC Fee")}</strong>
															</div>
														</If>
														<If
															condition={
																this
																	.hasEnforceV2NonrkpayInvoicingEnabled
															}
														>
															<div className="col-sm-3">
																<strong>
																	{lang.t("Manual Payments")}
																</strong>
															</div>
														</If>
													</div>
												</div>
												<div className="col-sm-2 text-center">
													<strong>{lang.t("Fees")}</strong>
												</div>
											</div>
											<div className="table-overflow">
												<For
													each="client"
													of={clients}
												>
													<div
														key={`orgclient-${client.Id}`}
														className="row v-align-sm"
													>
														<div className="col-sm-2">
															<span className="visible-xs-inline">
																ID&nbsp;
															</span>
															{client.Id}
														</div>
														<div className="col-sm-2 visible-xs">
															<span>
																{lang.t("Default Invoicing")}:&nbsp;
															</span>
															<strong>
																{this.formatJobInvoicedType(
																	client.JobInvoicedTypeId,
																)}
															</strong>
														</div>
														<div className="col-sm-3 staff-email">
															{client.Name || ""}
														</div>
														<div className="col-sm-2 hidden-xs">
															{this.formatJobInvoicedType(
																client.JobInvoicedTypeId,
															)}
														</div>
														<div className="col-sm-3">
															<div className="row toogle-row">
																<If
																	condition={this.isFinancialAdmin()}
																>
																	<div className="col-xs-3">
																		<Switch
																			on={
																				client.FloatInvoicePayEnabled
																			}
																			onClick={this.toggleClientSettings(
																				client.Id,
																				{
																					instantPay:
																						client.SameDayPayEnabled,
																					floatPay:
																						!client.FloatInvoicePayEnabled,
																					isWorkerCompensationFeeEnabled:
																						client?.IsWorkerCompensationFeeEnabled,
																					enforceV2NonRKPayInvoicing:
																						client?.EnforceV2NonRKPayInvoicing,
																				},
																			)}
																		/>
																		<br className="visible-xs" />
																		<small className="visible-xs">
																			{lang.t("Float")}
																		</small>
																	</div>
																</If>
																<div className="col-xs-3">
																	<Switch
																		on={
																			client.SameDayPayEnabled
																		}
																		onClick={this.toggleClientSettings(
																			client.Id,
																			{
																				instantPay:
																					!client.SameDayPayEnabled,
																				floatPay:
																					client.FloatInvoicePayEnabled,
																				isWorkerCompensationFeeEnabled:
																					client?.IsWorkerCompensationFeeEnabled,
																				enforceV2NonRKPayInvoicing:
																					client?.EnforceV2NonRKPayInvoicing,
																			},
																		)}
																		disabled={
																			!details.SameDayPayEnabled &&
																			!details.InstantPayEnabledV3
																		}
																	/>
																	<br className="visible-xs" />
																	<small className="visible-xs">
																		{lang.t("Instant Pay")}
																	</small>
																</div>
																<If
																	condition={
																		this
																			.hasWorkersCompensationFee &&
																		details.WorkerCompensationFeeEnabled
																	}
																>
																	<div className="col-xs-3">
																		<Switch
																			on={
																				client.IsWorkerCompensationFeeEnabled
																			}
																			onClick={this.toggleClientSettings(
																				client.Id,
																				{
																					instantPay:
																						client.SameDayPayEnabled,
																					floatPay:
																						client.FloatInvoicePayEnabled,
																					isWorkerCompensationFeeEnabled:
																						!client.IsWorkerCompensationFeeEnabled,
																					enforceV2NonRKPayInvoicing:
																						client?.EnforceV2NonRKPayInvoicing,
																				},
																			)}
																		/>
																		<br className="visible-xs" />
																		<small className="visible-xs">
																			{lang.t("WC Fee")}
																		</small>
																	</div>
																</If>
																<If
																	condition={
																		this
																			.hasEnforceV2NonrkpayInvoicingEnabled
																	}
																>
																	<div className="col-xs-3">
																		{checkingClientFutureJobs[client.Id] ? (
																			<div className="text-center">
																				<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
																			</div>
																		) : (
																			<Switch
																				on={
																					client.EnforceV2NonRKPayInvoicing
																				}
																				onClick={(event) => this.handleOnClickEnforceV2NonRKPayInvoicing(client, event)}
																				disabled={
																					!details.InvoicingV3Enabled ||
																					checkingClientFutureJobs[client.Id]
																				}
																			/>
																		)}
																		<br className="visible-xs" />
																		<small className="visible-xs">
																			{lang.t(
																				"Manual Payments",
																			)}
																		</small>
																	</div>
																</If>
															</div>
														</div>
														<div className="col-sm-2 text-center">
															<If
																condition={
																	this.hasClientLevelFees &&
																	Type !== CLIENT
																}
															>
																<button
																	type="button"
																	className="btn btn-primary"
																	onClick={() => {
																		this.selectClient(client);
																		this.toggleConfigureClientFeesModal();
																	}}
																>
																	{lang.t("Configure")}
																</button>
															</If>
														</div>
													</div>
												</For>
												<If condition={clientHasMore}>
													<ScrollDetect
														className="text-center"
														onVisible={this.getOrganizationClients}
													>
														<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
													</ScrollDetect>
												</If>
												<If
													condition={
														!clientHasMore && clients.length === 0
													}
												>
													<div className="text-muted text-center">
														{lang.t("No clients found")}
													</div>
												</If>
											</div>
										</div>
									</If>
								</div>
							</div>
						</When>
					</Choose>
				</div>
				<EditImageModal
					show={showEditImageModal}
					closeModal={this.closeEditImageModal}
					successCallback={this.uploadImage}
					imageToEdit={imageToEdit}
				/>
				<OrganizationFeesModal
					show={showOrganizationFeesModal}
					onHide={this.toggleConfigureFeesModal}
					effectiveServiceFees={EffectiveServiceFees}
					processingFees={processingFees}
					rkPayRateHistory={rkPayRateHistory}
					pFeeRateHistory={newPFeeRateHistory}
					details={details}
					type={Type}
					onSaveServiceFee={this.updateProcessingFees}
					onSaveProcessingFee={this.saveRkPayRateChanges}
				/>
				<ClientFeesModal
					show={showClientFeesModal}
					onHide={this.toggleConfigureClientFeesModal}
					client={selectedClient}
					organizationId={organizationId}
					details={details}
					type={Type}
				/>
				<OfficerSyncModal
					show={showOfficerSync}
					hubspot={hubspot}
					syncType={syncType}
					onHide={this.onHideOfficerSync}
					jurisdictionId={details.Jurisdiction?.Id}
					organizationId={organizationId}
					details={details}
				/>
				<ImpersonateModal
					userIdFromList={impersonatingUser}
					show={impersonatingUser !== undefined}
					toggleModal={this.toggleImpersonateModal}
				/>
				<EditAccountModal
					accountType="user"
					user={user}
					ref={this._editUser}
					onFinished={() =>
						this.setState({ pageNumber: 1 }, () => this.getOrganizationUsers())
					}
				/>
				<OrganizationSettingsModal
					show={showOrganizationSettingsModal}
					details={details}
					Type={Type}
					closeModal={this.toggleOrganizationSettingsModal}
					updateDetails={this.updateDetails}
					isFinancialAdmin={this.isFinancialAdmin}
					uploadDocument={this.uploadDocument}
				/>
				<RefundModal
					show={showRefundModal}
					closeModal={this.toggleRefundModal}
					payment={payment}
					orgBalance={currentBalance}
				/>
				<EditSignupModal
					show={showEditSignupModal}
					organization={details}
					onHide={this.toggleEditSignupModal}
					onSuccess={this.getOrganizationDetails}
				/>
				<ReportAdminModal
					show={showReportAdminModal}
					onHide={this.toggleReportAdminModal}
					organization={details}
					user={user}
					onSaveReport={this.updateReport}
                    refreshOrganization={this.getOrganizationDetails}
				/>
			</Fragment>
		);
	}
}

OrganizationDetails.propTypes = {
	router: PropTypes.object.isRequired,
	user: PropTypes.object,
	params: PropTypes.object,
	flags: PropTypes.object.isRequired,
};

OrganizationDetails.defaultProps = {
	user: {},
	params: undefined,
};

export default withLDConsumer()(OrganizationDetails);
